"use client";

import { FC } from "react";
import Script from "next/script";
import {
  logAdobePageBottomError,
  logAdobeSatelliteUnavailable,
  logAdobeScriptLoadError,
  runBrowserCookieDiagnostics,
  startReferrerCookieDiagnostics,
} from "@/helpers/analyticsDiagnostics";

const AnalyticsScript: FC<{ src: string }> = ({ src }) => {
  const registerClarityVariationCall = () => {
    const waitForCondition = (condition: () => boolean) =>
      new Promise<void>((resolve, reject) => {
        const deadline = Date.now() + 5000;
        const check = () => {
          if (condition()) {
            resolve();
            return;
          }

          const remaining = deadline - Date.now();
          if (remaining <= 0) {
            reject(new Error("Condition timed out"));
            return;
          }

          setTimeout(check, Math.min(100, remaining));
        };
        check();
      });

    const clarityCheckPromise = waitForCondition(
      () => typeof window.clarity === "function",
    );
    const personalizationValueCheckPromise = waitForCondition(() =>
      Boolean(window.dataLayer?.personalization?.variation),
    );

    Promise.all([clarityCheckPromise, personalizationValueCheckPromise])
      .then(() => {
        console.log(
          "CLARITY AND PERSONALIZATION VALUE AVAILABLE",
          window.clarity,
          window.dataLayer.personalization?.variation,
        );
        window.clarity!(
          "set",
          "builderVariation",
          window.dataLayer.personalization?.variation,
        );
      })
      .catch(() => {
        console.log(
          "Clarity or personalization value not available, skipping clarity variation call.",
        );
      });
  };

  const registerCallBottom = () => {
    const callPageBottom = () => {
      runBrowserCookieDiagnostics();
      startReferrerCookieDiagnostics();
      registerClarityVariationCall();

      if (typeof window._satellite?.pageBottom === "function") {
        try {
          window._satellite.pageBottom();
        } catch (error) {
          logAdobePageBottomError(error);
        }
      } else {
        logAdobeSatelliteUnavailable(src);
      }
    };

    if (document.readyState === "complete") {
      callPageBottom();
    } else {
      window.addEventListener("load", callPageBottom);
    }
  };

  return (
    <Script
      src={src}
      onError={(error) => logAdobeScriptLoadError(src, error)}
      onLoad={registerCallBottom}
      strategy="afterInteractive"
    />
  );
};

export default AnalyticsScript;
